import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
} from "@mui/material";
import { useHistory, useLocation, Switch, Route } from "react-router-dom";
import {
  Group,
  School,
  Title,
  Workspaces,
  Menu as MenuIcon,
  HowToReg,
  Rule,
  Money,
  MilitaryTech,
  Groups,
} from "@mui/icons-material";
import CodeTable from "./CodeTable";
import { codePaths } from "../../utils/enums";
import { PATH_CODE_SETTING } from "../../utils/routeConstants";
import { getAccessPermission } from "../../reducers/PermissionsReducer";

const drawerWidth = 240;
const collapsedDrawerWidth = 60;
const path = PATH_CODE_SETTING;

// always add new menu item in alphabetical order
const dynamicMenuItems = [
  {
    label: "Applicant Status",
    path: codePaths.APPLICANT_STATUS,
    icon: <HowToReg />,
    permission: { area: "codes", key: "Applicant Status" },
  },
  {
    label: "Applicant Test Result",
    path: codePaths.APPLICANT_TEST_RESULT,
    icon: <Rule />,
    permission: { area: "codes", key: "Applicant Test Result" },
  },
  {
    label: "Department/Division",
    path: codePaths.DEPARTMENT,
    icon: <Group />,
    permission: { area: "codes", key: "Department" },
  },
  {
    label: "Education",
    path: codePaths.EDUCATION,
    icon: <School />,
    permission: { area: "codes", key: "Education" },
  },
  {
    label: "Employment Status",
    path: codePaths.EMPLOYMENT_STATUS,
    icon: <Rule />,
    permission: { area: "codes", key: "Employment Status" },
  },
  {
    label: "Rank",
    path: codePaths.RANK,
    icon: <MilitaryTech />,
    permission: { area: "codes", key: "Rank" },
  },
  {
    label: "Relationship",
    path: codePaths.Relationship,
    icon: <Groups />,
    permission: { area: "codes", key: "Relationship" },
  },
  {
    label: "Section",
    path: codePaths.SECTION,
    icon: <Workspaces />,
    permission: { area: "codes", key: "Section" },
  },
  {
    label: "Title/Position",
    path: codePaths.TITLE,
    icon: <Title />,
    permission: { area: "codes", key: "Title" },
  },
  {
    label: "Training Cost",
    path: codePaths.TRAINING_COST,
    icon: <Money />,
    permission: { area: "codes", key: "Training Cost" },
  },
];

const codeRoutes = [
  {
    path: codePaths.APPLICANT_STATUS,
    serviceName: "code-per-applicant-status",
    stateName: "applicantStatusCodes",
    tableName: "Applicant Status Codes",
    columnOrderKey: "applicantStatusCodeTableColumnOrder",
    columnSizeKey: "applicantStatusCodeTableColumnSizes",
    columnVisibilityKey: "applicantStatusCodeTableColumnVisibility",
    tableSortModelKey: "applicantStatusCodeTableSortModel",
    title: "Applicant Status",
  },
  {
    path: codePaths.APPLICANT_TEST_RESULT,
    serviceName: "code-per-applicant-test-result",
    stateName: "applicantTestResultCodes",
    tableName: "Applicant Test Result Codes",
    columnOrderKey: "applicantTestResultCodeTableColumnOrder",
    columnSizeKey: "applicantTestResultCodeTableColumnSizes",
    columnVisibilityKey: "applicantTestResultCodeTableColumnVisibility",
    tableSortModelKey: "applicantTestResultCodeTableSortModel",
    title: "Applicant Test Result",
  },
  {
    path: codePaths.EDUCATION,
    serviceName: "code-per-education",
    stateName: "educationCodes",
    tableName: "Education Codes",
    columnOrderKey: "educationCodeTableColumnOrder",
    columnSizeKey: "educationCodeTableColumnSizes",
    columnVisibilityKey: "educationCodeTableColumnVisibility",
    tableSortModelKey: "educationCodeTableSortModel",
    title: "Education",
  },
  {
    path: codePaths.DEPARTMENT,
    serviceName: "code-per-department",
    stateName: "departmentCodes",
    tableName: "Department Codes",
    columnOrderKey: "departmentCodeTableColumnOrder",
    columnSizeKey: "departmentCodeTableColumnSizes",
    columnVisibilityKey: "departmentCodeTableColumnVisibility",
    tableSortModelKey: "departmentCodeTableSortModel",
    title: "Department/Division",
  },
  {
    path: codePaths.SECTION,
    serviceName: "code-per-section",
    stateName: "sectionCodes",
    tableName: "Section Codes",
    columnOrderKey: "sectionCodeTableColumnOrder",
    columnSizeKey: "sectionCodeTableColumnSizes",
    columnVisibilityKey: "sectionCodeTableColumnVisibility",
    tableSortModelKey: "sectionCodeTableSortModel",
    title: "Section",
  },
  {
    path: codePaths.TITLE,
    serviceName: "code-per-title-position",
    stateName: "titlePositionCodes",
    tableName: "Title/Position Codes",
    columnOrderKey: "titlePositionCodeTableColumnOrder",
    columnSizeKey: "titlePositionCodeTableColumnSizes",
    columnVisibilityKey: "titlePositionCodeTableColumnVisibility",
    tableSortModelKey: "titlePositionCodeTableSortModel",
    title: "Title/Position",
  },
  {
    path: codePaths.EMPLOYMENT_STATUS,
    serviceName: "code-per-employment-status",
    stateName: "employmentStatusCodes",
    tableName: "Employment Status Codes",
    columnOrderKey: "employmentStatusCodeTableColumnOrder",
    columnSizeKey: "employmentStatusCodeTableColumnSizes",
    columnVisibilityKey: "employmentStatusCodeTableColumnVisibility",
    tableSortModelKey: "employmentStatusCodeTableSortModel",
    title: "Employment Status",
  },
  {
    path: codePaths.TRAINING_COST,
    serviceName: "code-per-training-cost",
    stateName: "trainingCostCodes",
    tableName: "Training Cost Codes",
    columnOrderKey: "trainingCostCodeTableColumnOrder",
    columnSizeKey: "trainingCostCodeTableColumnSizes",
    columnVisibilityKey: "trainingCostCodeTableColumnVisibility",
    tableSortModelKey: "trainingCostCodeTableSortModel",
    title: "Training Cost",
  },
  {
    path: codePaths.RANK,
    serviceName: "code-per-rank",
    stateName: "rankCodes",
    tableName: "Rank Codes",
    columnOrderKey: "rankCodeTableColumnOrder",
    columnSizeKey: "rankCodeTableColumnSizes",
    columnVisibilityKey: "rankCodeTableColumnVisibility",
    tableSortModelKey: "rankCodeTableSortModel",
    title: "Rank",
  },
  {
    path: codePaths.Relationship,
    serviceName: "code-per-relationships",
    stateName: "relationshipsCodes",
    tableName: "Relationships Codes",
    columnOrderKey: "relationshipsCodeTableColumnOrder",
    columnSizeKey: "relationshipsCodeTableColumnSizes",
    columnVisibilityKey: "relationshipsCodeTableColumnVisibility",
    tableSortModelKey: "relationshipsCodeTableSortModel",
    title: "Relationship",
  },
];

const Codes = () => {
  const history = useHistory();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
  const userAgency = useSelector((s) => s.user.userAgency);

  const hasRead = (item) => {
    if (item.alwaysVisible) return true;

    if (!item.permission || !item.permission.area || !item.permission.key) {
      return false;
    }

    const permObj = getAccessPermission(
      item.permission.area,
      item.permission.key,
      userAgency
    );
    return !!permObj?.Read;
  };

  const allowedMenu = dynamicMenuItems
    .map((item) => {
      if (item.subMenu) {
        const filteredSub = item.subMenu.filter((s) => hasRead(s));
        const parentAllowed = hasRead(item);
        return parentAllowed ? { ...item, subMenu: filteredSub } : null;
      } else {
        return hasRead(item) ? item : null;
      }
    })
    .filter(Boolean);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    setIsDrawerCollapsed(!isDrawerCollapsed);
  };

  const drawerContent = (
    <Box sx={{ overflow: "auto" }}>
      <List>
        {allowedMenu.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => {
                history.push(`${path}/${item.path}`);
                if (mobileOpen) setMobileOpen(false);
              }}
              selected={location.pathname === `${path}/${item.path}`}
              sx={{
                justifyContent: isDrawerCollapsed ? "center" : "initial",
                px: isDrawerCollapsed ? 1 : 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: isDrawerCollapsed ? "auto" : 56,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!isDrawerCollapsed && <ListItemText primary={item.label} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          width: isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth,
            boxSizing: "border-box",
            transition: "width 0.2s",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton onClick={handleDrawerCollapse}>
            <MenuIcon />
          </IconButton>
        </Box>
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2 },
          width: {
            sm: `calc(100% - ${
              isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth
            }px)`,
          },
          transition: "width 0.2s",
        }}
      >
        <AppBar
          position="static"
          sx={{ display: { xs: "block", sm: "none" }, mb: 2 }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap>
              Code Settings
            </Typography>
          </Toolbar>
        </AppBar>
        <Switch>
          {codeRoutes.map((route) => (
            <Route
              key={route.path}
              path={`${path}/${route.path}`}
              render={() => (
                <CodeTable
                  serviceName={route.serviceName}
                  stateName={route.stateName}
                  tableName={route.tableName}
                  columnOrderKey={route.columnOrderKey}
                  columnSizeKey={route.columnSizeKey}
                  columnVisibilityKey={route.columnVisibilityKey}
                  tableSortModelKey={route.tableSortModelKey}
                  title={route.title}
                />
              )}
            />
          ))}
          <Route path={path}>
            <div></div>
          </Route>
        </Switch>
      </Box>
    </Box>
  );
};

export default Codes;
